import { RefObject } from 'react';
import { Flex, Tab, SrOnly } from '@servicetitan/anvil2';
import { usePerTabHorizontalScroll } from './usePerTabHorizontalScroll';

export interface MidInfo {
    merchantId: string;
    midName: string | null;
}

export interface MidTabsProps {
    mids: MidInfo[];
    /** Must always match one entry in `mids`; parent is the source of truth. */
    selectedMerchantId: string;
    onMidChange: (merchantId: string) => void;
    scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

/**
 * MID selector tabs using Anvil2 Tab.
 * Tracks the selected MID and notifies the parent for filtering; manages scroll position per MID.
 */
export const MidTabs = ({
    mids,
    selectedMerchantId,
    onMidChange,
    scrollContainerRef,
}: MidTabsProps) => {
    const { beginTabTransition } = usePerTabHorizontalScroll(
        scrollContainerRef,
        selectedMerchantId,
    );

    const selectedIndex = mids.findIndex(m => m.merchantId === selectedMerchantId);
    const tabIndex = selectedIndex === -1 ? 0 : selectedIndex;

    const handleMidChange = (merchantId: string) => {
        beginTabTransition(merchantId, () => {
            onMidChange(merchantId);
        });
    };

    const handleIndexChange = (index: number) => {
        const mid = mids[index];
        if (mid) {
            handleMidChange(mid.merchantId);
        }
    };

    if (mids.length === 0) {
        return null;
    }

    return (
        <Flex direction="column" gap="3">
            <Tab
                index={tabIndex}
                onIndexChange={handleIndexChange}
                fill
                style={{ gap: 'revert', width: '100%' }}
            >
                <Tab.List>
                    {mids.map(mid => (
                        <Tab.Button
                            key={mid.merchantId}
                            id={`mid-tab-${mid.merchantId}`}
                            controls={`mid-panel-${mid.merchantId}`}
                        >
                            {mid.midName ?? mid.merchantId}
                        </Tab.Button>
                    ))}
                </Tab.List>
                {mids.map(mid => (
                    <Tab.Panel
                        key={mid.merchantId}
                        id={`mid-panel-${mid.merchantId}`}
                        style={{ padding: 0, margin: 0, minHeight: 0 }}
                    >
                        <SrOnly>
                            {mid.midName ?? mid.merchantId}. Plans for this merchant are listed
                            below.
                        </SrOnly>
                    </Tab.Panel>
                ))}
            </Tab>
        </Flex>
    );
};
